import { SAGA_CHAPTERS, SAGA_SWITCH, SIDES, type SideId } from '@somnhold/content';
import { dayOmen, sagaBlocked, sagaOf, type CampaignState } from '@somnhold/sim';
import { music } from '../audio/music.ts';
import { t } from '../i18n.ts';
import { h } from '../ui/dom.ts';

/**
 * Pre-made cinematics (docs/TODO.md): short videos made once and kept as files, shown once to each
 * player when their moment comes, then kept in a list to watch again. Zero AI cost per game.
 */
export interface Cinematic {
  id: string;
  src: string;
  poster: string;
  /** i18n keys: a kicker, a title, and lines shown over the video. */
  kicker: string;
  title: string;
  lines: string[];
  /** Whether it is this campaign's moment for it (today's day index). */
  due: (s: CampaignState, day: number) => boolean;
}

/** The side saga chapters that have a video (`side-<saga>-<chapter>.mp4`). */
const SIDE_VIDEOS: [SideId, string][] = [
  ['faces', 'back'],
  ['faces', 'shadow'],
  ['faces', 'accuse'],
  ['horn', 'hunt'],
  ['horn', 'alone'],
  ['faces', 'mada'],
  ['burn', 'patrol'],
  ['rails', 'descent'],
  ['rails', 'winder'],
  ['rails', 'anvil'],
  ['rails', 'rising'],
];

export const CINEMATICS: Cinematic[] = [
  {
    id: 'omen-ink',
    src: '/cinematics/omen-ink.mp4',
    poster: '/cinematics/omen-ink.jpg',
    kicker: 'cine.omenInk.kicker',
    title: 'cine.omenInk.title',
    lines: ['cine.omenInk.l1', 'cine.omenInk.l2'],
    due: (s, day) => s.status === 'active' && dayOmen(s, day) === 'ink',
  },
  {
    id: 'omen-fog',
    src: '/cinematics/omen-fog.mp4',
    poster: '/cinematics/omen-fog.jpg',
    kicker: 'cine.omenFog.kicker',
    title: 'cine.omenFog.title',
    lines: ['cine.omenFog.l1', 'cine.omenFog.l2'],
    due: (s, day) => s.status === 'active' && dayOmen(s, day) === 'fog',
  },
  {
    id: 'omen-storm',
    src: '/cinematics/omen-storm.mp4',
    poster: '/cinematics/omen-storm.jpg',
    kicker: 'cine.omenStorm.kicker',
    title: 'cine.omenStorm.title',
    lines: ['cine.omenStorm.l1', 'cine.omenStorm.l2'],
    due: (s, day) => s.status === 'active' && dayOmen(s, day) === 'storm',
  },
  {
    id: 'omen-frenzy',
    src: '/cinematics/omen-frenzy.mp4',
    poster: '/cinematics/omen-frenzy.jpg',
    kicker: 'cine.omenFrenzy.kicker',
    title: 'cine.omenFrenzy.title',
    lines: ['cine.omenFrenzy.l1', 'cine.omenFrenzy.l2'],
    due: (s, day) => s.status === 'active' && dayOmen(s, day) === 'frenzy',
  },
  {
    id: 'omen-starfall',
    src: '/cinematics/omen-starfall.mp4',
    poster: '/cinematics/omen-starfall.jpg',
    kicker: 'cine.omenStarfall.kicker',
    title: 'cine.omenStarfall.title',
    lines: ['cine.omenStarfall.l1', 'cine.omenStarfall.l2'],
    due: (s, day) => s.status === 'active' && dayOmen(s, day) === 'starfall',
  },
  {
    id: 'omen-bloodMoon',
    src: '/cinematics/omen-bloodMoon.mp4',
    poster: '/cinematics/omen-bloodMoon.jpg',
    kicker: 'cine.omenBloodMoon.kicker',
    title: 'cine.omenBloodMoon.title',
    lines: ['cine.omenBloodMoon.l1', 'cine.omenBloodMoon.l2'],
    due: (s, day) => s.status === 'active' && dayOmen(s, day) === 'bloodMoon',
  },
  {
    id: 'omen-swarm',
    src: '/cinematics/omen-swarm.mp4',
    poster: '/cinematics/omen-swarm.jpg',
    kicker: 'cine.omenSwarm.kicker',
    title: 'cine.omenSwarm.title',
    lines: ['cine.omenSwarm.l1', 'cine.omenSwarm.l2'],
    due: (s, day) => s.status === 'active' && dayOmen(s, day) === 'swarm',
  },
  {
    id: 'omen-frost',
    src: '/cinematics/omen-frost.mp4',
    poster: '/cinematics/omen-frost.jpg',
    kicker: 'cine.omenFrost.kicker',
    title: 'cine.omenFrost.title',
    lines: ['cine.omenFrost.l1', 'cine.omenFrost.l2'],
    due: (s, day) => s.status === 'active' && dayOmen(s, day) === 'frost',
  },
  // The saga: each chapter opens with its own.
  ...['garrison', 'wall', 'fire', 'deep', 'postern', 'maelis', 'burn'].map(
    (id): Cinematic => ({
      id: `saga-${id}`,
      src: `/cinematics/saga-${id}.mp4`,
      poster: `/cinematics/saga-${id}.jpg`,
      kicker: `cine.saga.${id}.kicker`,
      title: `saga.${id}.title`,
      lines: [`cine.saga.${id}.l1`],
      due: (s, day) => {
        const i = SAGA_CHAPTERS.findIndex((c) => c.id === id);
        return SAGA_SWITCH.on && sagaOf(s).chapter === i && sagaBlocked(s, day) === null;
      },
    }),
  ),
  // The side sagas: a chapter opens with its own when its first scene does (never over the bastion: it would
  // show a chapter the group hasn't reached by its story). Only the chapters with a video are listed.
  ...SIDE_VIDEOS.map(
    ([saga, chapter]): Cinematic => ({
      id: `side-${saga}-${chapter}`,
      src: `/cinematics/side-${saga}-${chapter}.mp4`,
      poster: `/cinematics/side-${saga}-${chapter}.jpg`,
      kicker: `side.${saga}.title`,
      title: `side.${saga}.${chapter}.title`,
      lines: [`cine.side.${saga}.${chapter}.l1`],
      due: () => false,
    }),
  ),
];

/** The side saga chapter's cinematic, if it has one. */
export const sideCinematic = (saga: SideId, chapter: number): Cinematic | undefined => {
  const ch = SIDES.find((x) => x.id === saga)?.chapters[chapter];
  return ch ? CINEMATICS.find((c) => c.id === `side-${saga}-${ch.id}`) : undefined;
};

const key = (id: string): string => `somnhold.cine.${id}`;

export const cinematicSeen = (id: string): boolean => {
  try {
    return localStorage.getItem(key(id)) === '1';
  } catch {
    // Private mode: never forced on anyone twice in a session is good enough.
    return true;
  }
};

const markSeen = (id: string): void => {
  try {
    localStorage.setItem(key(id), '1');
  } catch {
    /* private mode */
  }
};

/** The cinematic this player should see now, once: the first one whose moment has come and they haven't seen. */
export const dueCinematic = (s: CampaignState, day: number): Cinematic | null => CINEMATICS.find((c) => !cinematicSeen(c.id) && c.due(s, day)) ?? null;

/** The ones this player already saw, to watch again. */
export const seenCinematics = (): Cinematic[] => CINEMATICS.filter((c) => cinematicSeen(c.id));

/**
 * Plays a cinematic over whatever is on screen: the video, its words fading in over it, a way to skip.
 * The music steps aside while it plays. `onDone` runs when it ends or is skipped.
 */
export const playCinematic = (c: Cinematic, onDone: () => void = () => {}): void => {
  markSeen(c.id);
  void music.play('silent');
  const video = h('video.cine-video', { playsinline: '', preload: 'auto', poster: c.poster }) as HTMLVideoElement;
  video.src = c.src;
  const words = h('div.cine-words', {}, h('span.cine-kicker', {}, t(c.kicker)), h('h2', {}, t(c.title)), ...c.lines.map((l) => h('p', {}, t(l))));
  const play = h('button.btn.cine-play', { onclick: () => void start() }, t('cine.play'));
  let closed = false;
  const close = (): void => {
    if (closed) return;
    closed = true;
    video.pause();
    el.classList.add('out');
    window.setTimeout(() => el.remove(), 400);
    onDone();
  };
  const el = h(
    'div.cine',
    { role: 'dialog', 'aria-modal': 'true', 'aria-label': t(c.title), 'data-test': 'cinematic' },
    video,
    words,
    play,
    h('button.btn.ghost.cine-skip', { onclick: close, 'data-test': 'cine-skip' }, t('cine.skip')),
  );
  const start = async (): Promise<void> => {
    play.classList.add('hidden');
    try {
      await video.play();
      el.classList.add('playing');
    } catch {
      // Autoplay with sound refused (no tap yet): a tap starts it.
      play.classList.remove('hidden');
    }
  };
  video.addEventListener('ended', () => {
    el.classList.add('ended');
    window.setTimeout(close, 2600);
  });
  document.body.append(el);
  void start();
};

/** A small list of the cinematics already seen, to watch them again. */
export const cinematicsModal = (): void => {
  const list = seenCinematics();
  const back = h('div.modal-back', { onclick: (ev: Event) => { if (ev.target === back) back.remove(); } });
  const pick = (c: Cinematic): void => {
    back.remove();
    playCinematic(c);
  };
  back.append(
    h(
      'div.modal',
      { role: 'dialog', 'aria-modal': 'true', 'data-test': 'cinematics' },
      h('header', {}, h('h2', {}, t('cine.list'))),
      h(
        'div.body',
        {},
        ...(list.length
          ? list.map((c) => h('button.cine-item', { onclick: () => pick(c) }, h('img', { src: c.poster, alt: '' }), h('span', {}, h('small', {}, t(c.kicker)), h('b', {}, t(c.title)))))
          : [h('p', {}, t('cine.none'))]),
      ),
      h('footer', {}, h('button.btn', { onclick: () => back.remove() }, t('cine.close'))),
    ),
  );
  document.body.append(back);
};
